
import React, { useState, useCallback } from "react";
import {
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToWord,
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from "axios";
import ExcelJS from "exceljs";
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";

export default function TrialBalance() {
  //#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  //#endregion

  //#region Variables
  const [company, setCompany] = useState(null);

  // Default dates: beginning of current year to today
  const today = new Date();
  const localDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [dateFrom, setDateFrom] = useState(
    `${today.getFullYear()}-01-01`
  );
  const [dateTo, setDateTo] = useState(localDate(today));
  //#endregion

  //#region Generate

  const fetchReportData = useCallback(async () => {
    const body = {
      dateFrom,
      dateTo,
    };

    try {
      const response = await axios.post(
        `${apiUrl}/api/Report/report-data/trial-balance`,
        body,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching Trial Balance:", error);
      setFailMessage(
        error.response?.data?.message ||
        error.response?.data ||
        "Error fetching Trial Balance."
      );
      return null;
    }
  }, [dateFrom, dateTo]);

  const validateFilters = () => {
    if (!dateFrom || !dateTo || !company) {
      setFailMessage("Please fill up all fields.");
      return false;
    }

    if (new Date(dateFrom) > new Date(dateTo)) {
      setFailMessage("Date From cannot be later than Date To.");
      return false;
    }

    return true;
  };

  const getReportDetails = (response) => {
    // Supports the object response from the Trial Balance API.
    // Also supports an array response if your API returns one.
    const details = Array.isArray(response)
      ? response
      : response?.details ?? [];

    return {
      details,
      totalDebit: Array.isArray(response)
        ? details.reduce((sum, x) => sum + Number(x.debit || 0), 0)
        : Number(response?.totalDebit ?? 0),
      totalCredit: Array.isArray(response)
        ? details.reduce((sum, x) => sum + Number(x.credit || 0), 0)
        : Number(response?.totalCredit ?? 0),
      difference: Array.isArray(response)
        ? details.reduce((sum, x) => sum + Number(x.debit || 0), 0) -
          details.reduce((sum, x) => sum + Number(x.credit || 0), 0)
        : Number(response?.difference ?? 0),
    };
  };

  //#region PDF

  const generateTrialBalance = async () => {
    if (!validateFilters()) return;

    setLoading(true);

    try {
      const responseData = await fetchReportData();

      if (!responseData) return;

      const { details, totalDebit, totalCredit, difference } =
        getReportDetails(responseData);

      if (!details.length) {
        setFailMessage("No record found.");
        return;
      }

      const pdfUrl = `${window.location.origin}/docs/TRIAL_BALANCE.pdf`;

      const templateBytes = await fetch(pdfUrl).then((r) => {
        if (!r.ok) throw new Error("Trial Balance PDF template not found.");
        return r.arrayBuffer();
      });

      const templateDoc = await PDFDocument.load(templateBytes);
      const pdfDoc = await PDFDocument.load(templateBytes);

      pdfDoc.registerFontkit(fontkit);

      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";

        let truncated = String(text);
        const ellipsis = "...";

        while (
          truncated.length > 0 &&
          font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth
        ) {
          truncated = truncated.slice(0, -1);
        }

        return truncated.length < String(text).length
          ? truncated + ellipsis
          : String(text);
      };

      const drawHeader = (page) => {
        page.drawText(company.name?.toString() ?? "", {
          x: 35,
          y: 583,
          size: 16,
          font: calibriBold,
        });

        page.drawText(company.address?.toString() ?? "", {
          x: 35,
          y: 573,
          size: 8,
          font: calibriFont,
        });

        page.drawText(company.tin?.toString() ?? "", {
          x: 35,
          y: 563,
          size: 8,
          font: calibriFont,
        });

        page.drawText(
          `${FormatDateToWord(new Date(`${dateFrom}T00:00:00`))} to ${FormatDateToWord(new Date(`${dateTo}T00:00:00`))}`,
          {
            x: 85,
            y: 506,
            size: 8,
            font: calibriFont,
          }
        );

        page.drawText(FormatDateToWord(new Date()), {
          x: 85,
          y: 495,
          size: 8,
          font: calibriFont,
        });
      };

      let currentPage = pdfDoc.getPages()[0];
      drawHeader(currentPage);

      let yPosition = 446;
      const bottomThreshold = 80;
      const lineHeight = 12;

      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 446;
      };

      for (const record of details) {
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const accountNumber = truncateText(
          record.glAccountNumber ?? "",
          75,
          calibriFont,
          8
        );

        const accountName = truncateText(
          record.glAccountName ?? "",
          230,
          calibriFont,
          8
        );

        currentPage.drawText(accountNumber, {
          x: 40,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(accountName, {
          x: 155,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        const debit = Number(record.debit || 0);
        const credit = Number(record.credit || 0);

        if (debit !== 0) {
          currentPage.drawText(FormatValueToDecimal(debit), {
            x: 395,
            y: yPosition,
            size: 8,
            font: calibriFont,
          });
        }

        if (credit !== 0) {
          currentPage.drawText(FormatValueToDecimal(credit), {
            x: 585,
            y: yPosition,
            size: 8,
            font: calibriFont,
          });
        }

        yPosition -= lineHeight;
      }

      // Grand Total
      if (yPosition - 25 < bottomThreshold) {
        await addNewPage();
      }

      currentPage.drawLine({
        start: { x: 35, y: yPosition },
        end: { x: 757, y: yPosition },
        thickness: 1,
      });

      yPosition -= 15;

      currentPage.drawText("GRAND TOTAL", {
        x: 155,
        y: yPosition,
        size: 9,
        font: calibriBold,
      });

      currentPage.drawText(FormatValueToDecimal(totalDebit), {
        x: 395,
        y: yPosition,
        size: 8,
        font: calibriBold,
      });

      currentPage.drawText(FormatValueToDecimal(totalCredit), {
        x: 585,
        y: yPosition,
        size: 8,
        font: calibriBold,
      });

      yPosition -= 18;

      currentPage.drawText("DIFFERENCE", {
        x: 155,
        y: yPosition,
        size: 8,
        font: calibriBold,
      });

      currentPage.drawText(FormatValueToDecimal(difference), {
        x: 395,
        y: yPosition,
        size: 8,
        font: calibriBold,
      });

      // Page numbers
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;

      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 10,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], {
        type: "application/pdf",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "TRIAL_BALANCE.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Trial Balance PDF Error:", error);
      setFailMessage(error.message || "Error processing PDF.");
    } finally {
      setLoading(false);
    }
  };

  //#endregion

  //#region Excel

  const exportTrialBalanceExcel = async () => {
    if (!validateFilters()) return;

    setLoading(true);

    try {
      const responseData = await fetchReportData();

      if (!responseData) return;

      const { details, totalDebit, totalCredit, difference } =
        getReportDetails(responseData);

      if (!details.length) {
        setFailMessage("No record found.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Trial Balance");

      // Company Header
      worksheet.addRow([company.name?.toUpperCase()]).font = {
        bold: true,
        size: 14,
      };

      worksheet.addRow([company.address]);
      worksheet.addRow([`TIN: ${company.tin}`]);
      worksheet.addRow([]);

      // Report Header
      worksheet.addRow(["TRIAL BALANCE"]).font = {
        bold: true,
        size: 12,
      };

      worksheet.addRow([
        `Period: ${dateFrom} to ${dateTo}`,
      ]);

      worksheet.addRow([
        `Date Printed: ${FormatDateToWord(new Date())}`,
      ]);

      worksheet.addRow([]);

      // Table Header
      const headerRow = worksheet.addRow([
        "Account Number",
        "Account Name",
        "Debit",
        "Credit",
      ]);

      headerRow.eachCell((cell) => {
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" },
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "444444" },
        };

        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Details
      details.forEach((record) => {
        const row = worksheet.addRow([
          record.glAccountNumber ?? "",
          record.glAccountName ?? "N/A",
          Number(record.debit || 0),
          Number(record.credit || 0),
        ]);

        row.getCell(3).numFmt = "#,##0.00";
        row.getCell(4).numFmt = "#,##0.00";
      });

      // Grand Total
      const totalRow = worksheet.addRow([
        "",
        "GRAND TOTAL",
        totalDebit,
        totalCredit,
      ]);

      totalRow.font = { bold: true };

      for (let i = 2; i <= 4; i++) {
        const cell = totalRow.getCell(i);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFF00" },
        };

        cell.border = {
          top: { style: "thin" },
          bottom: { style: "double" },
        };
      }

      totalRow.getCell(3).numFmt = "#,##0.00";
      totalRow.getCell(4).numFmt = "#,##0.00";

      // Difference
      const differenceRow = worksheet.addRow([
        "",
        "DIFFERENCE",
        difference,
        "",
      ]);

      differenceRow.font = { bold: true };
      differenceRow.getCell(3).numFmt = "#,##0.00";

      // Column Widths
      worksheet.columns = [
        { width: 20 },
        { width: 45 },
        { width: 18 },
        { width: 18 },
      ];

      // Download
      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const filename = `Trial_Balance_${dateFrom}_${dateTo}.xlsx`;
      const link = document.createElement("a");

      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Trial Balance Excel Error:", error);
      setFailMessage("Error generating Excel file.");
    } finally {
      setLoading(false);
    }
  };

  //#endregion

  //#endregion

  //#region Access Template

  const accountData = JSON.parse(localStorage.getItem("accountData"));

  if (!accountData?.accessTemplate?.hasTrialBalance) {
    return <NoAccess />;
  }

  //#endregion

  //#region UI

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon
                fontSize="large"
                className="text-theme-color"
              />
              Trial Balance
            </h2>

            <p className="text-gray-500 mt-1">
              Debit and credit balances of all General Ledger accounts.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Date From <span className="text-red-500">*</span>
            </label>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Date To <span className="text-red-500">*</span>
            </label>

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Company <span className="text-red-500">*</span>
            </label>

            <CompanySelect
              data={company}
              setData={setCompany}
              isEditable={true}
            />
          </div>

        </div>

        <div className="mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 px-2">
            Generate Reports
          </h2>

          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-xs font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-2">Report Category</div>
            <div className="col-span-5">PDF Reports</div>
            <div className="col-span-5 border-l border-gray-100">
              Excel Reports
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="col-span-2">
                <span className="text-sm font-bold text-gray-700">
                  Summary
                </span>
              </div>

              <div className="col-span-5 flex flex-wrap gap-2">
                <button
                  onClick={generateTrialBalance}
                  className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all active:scale-95"
                >
                  PDF Summary
                </button>
              </div>

              <div className="col-span-5 flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0">
                <button
                  onClick={exportTrialBalanceExcel}
                  className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                >
                  Excel Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />

        <FailMessage
          message={failMessage}
          onClose={() => setFailMessage("")}
        />
      </div>

      {loading && <Loading />}
    </>
  );

  //#endregion
}
